import { FunctionComponent, MouseEventHandler } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { Link } from 'dibs-elements/exports/Link';
import { Badge } from 'dibs-elements/exports/Badge';
import { moreWaysToBrowseHeader } from '../../shared/sharedMessages';
import { SharedPopularSearchesLink } from '../SharedPopularSearchesLink/SharedPopularSearchesLink';
import { LINKS_BLOCK_DATA_TN } from '../../constants/sbConstants';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import classnames from 'classnames';
import styles from './SharedMobileLinksBlock.scss';
import type { SharedMobileLinksBlock_viewer } from './__generated__/SharedMobileLinksBlock_viewer.graphql';
import type { SharedMobileLinksBlock_itemSearch } from './__generated__/SharedMobileLinksBlock_itemSearch.graphql';
import type { SharedMobileLinksBlock_item } from './__generated__/SharedMobileLinksBlock_item.graphql';

type Props = {
    viewer: SharedMobileLinksBlock_viewer;
    itemSearch: SharedMobileLinksBlock_itemSearch;
    item: SharedMobileLinksBlock_item;
    links: Array<{ anchorText: string; url: string }>;
    title?: string;
    anchorTextPrefix?: string;
    onClick?: MouseEventHandler;
    borderTop?: boolean;
    borderBottom?: boolean;
};

const MIN_LINK_COUNT = 3;

export const SharedMobileLinksBlockComponent: FunctionComponent<Props> = ({
    viewer,
    itemSearch,
    item,
    links,
    title = moreWaysToBrowseHeader,
    anchorTextPrefix,
    onClick = () => {},
    borderTop,
    borderBottom,
}) => {
    if (!links?.length) {
        return null;
    }

    const validLinks = links.filter(link => link.url && link.anchorText);

    if (validLinks.length < MIN_LINK_COUNT) {
        return null;
    }

    const { isPopularSearchesEnabled } = viewer;
    const { popularSearchesLink } = itemSearch || item;

    const wrapperClassName = classnames(styles.wrapper, {
        [styles.borderTop]: borderTop,
        [styles.borderBottom]: borderBottom,
    });

    return (
        <HeadingLevel>
            {Heading => (
                <div className={wrapperClassName} data-tn={LINKS_BLOCK_DATA_TN}>
                    <Heading className={styles.header}>{title}</Heading>
                    <div className={styles.innerWrapper}>
                        <div className={styles.linkContainer}>
                            {validLinks.map(({ anchorText, url }) => (
                                <Link
                                    key={url}
                                    href={url}
                                    dataTn={`${LINKS_BLOCK_DATA_TN}-item`}
                                    className={`${styles.link} ${styles.specify}`}
                                    onClick={onClick}
                                >
                                    <Badge type="pill" size="medium-regular" interactive>
                                        {anchorTextPrefix
                                            ? `${anchorTextPrefix} ${anchorText}`
                                            : anchorText}
                                    </Badge>
                                </Link>
                            ))}
                        </div>
                    </div>
                    {isPopularSearchesEnabled && (
                        <SharedPopularSearchesLink popularSearchesLink={popularSearchesLink} />
                    )}
                </div>
            )}
        </HeadingLevel>
    );
};

export const SharedMobileLinksBlock = createFragmentContainer(SharedMobileLinksBlockComponent, {
    viewer: graphql`
        fragment SharedMobileLinksBlock_viewer on Viewer {
            isPopularSearchesEnabled: featureFlag(feature: "popularSearches")
        }
    `,
    itemSearch: graphql`
        fragment SharedMobileLinksBlock_itemSearch on ItemSearchQueryConnection {
            popularSearchesLink {
                ...SharedPopularSearchesLink_popularSearchesLink
            }
        }
    `,
    item: graphql`
        fragment SharedMobileLinksBlock_item on Item {
            popularSearchesLink {
                ...SharedPopularSearchesLink_popularSearchesLink
            }
        }
    `,
    links: graphql`
        fragment SharedMobileLinksBlock_links on InternalBuyLinkType @relay(plural: true) {
            url
            anchorText
        }
    `,
});
